import { useCallback, useEffect, useState } from 'react';
/**
 *
 * Returns size information from the window. On SSR it returns 0 for most of these values.
 * Optionally, you can provide a debounce time in milliseconds to limit the rate at which the window size is updated.
 *
 * ```js
 * import { useWindowSize } from '@polestar/component-warehouse-react';
 *
 * const MyComponent = () => {
 *    // Without debounce
 *    const { innerWidth, innerHeight, outerWidth, outerHeight, bodyWidth, bodyHeight } = useWindowSize();
 *
 *    // With debounce of 200 milliseconds
 *    const { innerWidth, innerHeight, outerWidth, outerHeight, bodyWidth, bodyHeight } = useWindowSize(200);
 *
 *    return ...
 * }
 * ```
 *
 * @storybook Hooks/useWindowSize
 */
const useWindowSize = (debounce = 0) => {
    const [windowSize, setWindowSize] = useState({
        innerWidth: 0,
        innerHeight: 0,
        outerWidth: 0,
        outerHeight: 0,
        bodyWidth: 0,
        bodyHeight: 0,
    });
    const debouncedUpdateSize = useCallback(() => {
        let timer;
        const updateSize = () => {
            setWindowSize({
                innerWidth: window.innerWidth,
                innerHeight: window.innerHeight,
                outerWidth: window.outerWidth,
                outerHeight: window.outerHeight,
                bodyWidth: document.body.clientWidth,
                bodyHeight: document.body.clientHeight,
            });
        };
        if (debounce > 0) {
            timer = setTimeout(updateSize, debounce);
            return () => clearTimeout(timer);
        }
        else {
            updateSize();
        }
    }, [debounce]);
    useEffect(() => {
        debouncedUpdateSize();
        window.addEventListener('resize', debouncedUpdateSize);
        return () => {
            window.removeEventListener('resize', debouncedUpdateSize);
        };
    }, [debouncedUpdateSize]);
    return windowSize;
};
export default useWindowSize;
